'use client';
import * as React from 'react';
import { Logo } from '@/components/logo';
import { Card } from './ui';
import { cn } from '@/lib/utils';

export function AuthPanel({
  title,
  subtitle,
  children,
  className,
  aside,
  header,
  footer,
  scenery = false,
}: {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Left photo panel — when set, renders the split composition. The aside owns
      its content (photo, quote card); the panel is inset and rounded, not full-bleed. */
  aside?: React.ReactNode;
  /** Brand block (wordmark + kicker ruler) rendered above the title on the form side. */
  header?: React.ReactNode;
  /** Bottom of the centred column page (the "Com carinho, Lanuq" credit on an invitation). */
  footer?: React.ReactNode;
  /** The sign-in: the pixel sky with cacti fills the page and the form is a card on it. */
  scenery?: boolean;
}) {
  // Centred column on the page background, brand on top (the sign-in page).
  if (!aside && (header || footer)) {
    return (
      <div
        className={cn(
          'flex min-h-screen flex-col',
          scenery ? 'bg-info bg-cover bg-bottom [image-rendering:pixelated]' : 'bg-background',
        )}
        // Half-size AVIF (29 KB), WebP as the fallback (39 KB); `pixelated` scales the art back
        // up without blur, so the smaller file costs no visible detail (owner, 2026-11-03).
        style={
          scenery
            ? { backgroundImage: "image-set(url('/brand/login-sky.avif') type('image/avif'), url('/brand/login-sky.webp') type('image/webp'))" }
            : undefined
        }
      >
        <div className={cn('flex flex-1 items-center justify-center py-12', scenery ? 'px-4' : 'px-6')}>
          <div
            className={cn(
              'w-full max-w-[400px]',
              // Frosted glass over the sky (owner, 2026-11-03), the same material as the credit
              // pill: the clouds show through, the fields stay solid white for legibility.
              scenery &&
                'max-w-[400px] rounded-[28px] bg-white/70 px-6 py-8 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_20px_50px_rgba(16,52,80,0.25)] backdrop-blur-xl backdrop-saturate-150 sm:px-8 sm:py-9',
              className,
            )}
          >
            {header}
            {title ? <h1 className="text-center text-[1.5rem] font-bold tracking-[-0.01em] text-foreground">{title}</h1> : null}
            {subtitle ? <p className="mt-1.5 text-center text-sm text-muted-foreground">{subtitle}</p> : null}
            <div className={cn(title || subtitle ? 'mt-6' : undefined)}>{children}</div>
          </div>
        </div>
        {footer}
      </div>
    );
  }

  // Split composition: inset rounded photo panel + form side carrying the brand.
  if (aside) {
    return (
      <div className="grid min-h-screen bg-background md:grid-cols-2">
        <div className="hidden p-6 md:block">
          <aside className="h-full">
            <div className="relative h-full min-h-[min(100vh-3rem,720px)] overflow-hidden rounded-2xl bg-medlux-strong">
              {aside}
            </div>
          </aside>
        </div>
        <div className="flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-[420px]">
            <div className={cn('w-full', className)}>
              {header ? <div>{header}</div> : null}
              {title ? (
                <h1 className="text-[1.625rem] font-bold tracking-[-0.01em] text-foreground">
                  {title}
                </h1>
              ) : null}
              {subtitle ? (
                <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>
              ) : null}
              <div className={cn(title || subtitle ? 'mt-6' : undefined)}>{children}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default: centered brand card (reset / change-password surfaces).
  return (
    <div className="flex min-h-screen items-center justify-center bg-medlux-strong px-4 py-10">
      <Card className={cn('w-full max-w-[440px] rounded-2xl p-7 shadow-2xl', className)}>
        <div className="mb-5 flex flex-col items-center text-center">
          <Logo size={24} />
          <h1 className="mt-4 text-[1.375rem] font-semibold text-foreground">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
        </div>
        <div>{children}</div>
      </Card>
    </div>
  );
}
